import { useState, useEffect } from 'react';
import adminApi from '@/lib/adminApi';
import { SystemHealthCard } from './components/SystemHealthCard';

interface KpiData {
  tenants: {
    total: number;
    active: number;
    suspended: number;
    newThisMonth: number;
    growthRate: number | null;
  };
  users: {
    total: number;
    activeThisMonth: number;
  };
  plans: Record<string, number>;
  whatsapp: {
    connected: number;
    total: number;
    connectionRate: number;
  };
  lgpd: {
    pending: number;
  };
}

function KpiCard({
  label,
  value,
  sub,
  color = 'text-gray-900',
}: {
  label: string;
  value: string | number;
  sub?: string;
  color?: string;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <p className="text-sm text-gray-500">{label}</p>
      <p className={`mt-1 text-3xl font-bold ${color}`}>{value}</p>
      {sub && <p className="mt-1 text-xs text-gray-500">{sub}</p>}
    </div>
  );
}

export function AdminDashboardPage() {
  const [kpis, setKpis] = useState<KpiData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi
      .get('/kpis/dashboard')
      .then(({ data }) => setKpis(data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  if (!kpis) {
    return <div className="p-8 text-gray-500">Não foi possível carregar os KPIs.</div>;
  }

  const planOrder = ['trial', 'core', 'pro', 'enterprise', 'suspended'];

  return (
    <div className="p-6">
      <h1 className="mb-6 text-xl font-semibold text-gray-900">Dashboard</h1>

      {/* Saúde do sistema (erros de produção — semáforo para leigo) */}
      <div className="mb-6">
        <SystemHealthCard />
      </div>

      {/* Clínicas */}
      <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
        Clínicas
      </h2>
      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Total" value={kpis.tenants.total} />
        <KpiCard label="Ativos" value={kpis.tenants.active} color="text-green-600" />
        <KpiCard label="Suspensos" value={kpis.tenants.suspended} color={kpis.tenants.suspended > 0 ? 'text-red-600' : 'text-gray-900'} />
        <KpiCard
          label="Novos este mês"
          value={kpis.tenants.newThisMonth}
          sub={
            kpis.tenants.growthRate !== null
              ? `${kpis.tenants.growthRate >= 0 ? '+' : ''}${kpis.tenants.growthRate}% vs mês anterior`
              : undefined
          }
          color={
            kpis.tenants.growthRate !== null && kpis.tenants.growthRate >= 0
              ? 'text-green-600'
              : 'text-gray-900'
          }
        />
      </div>

      {/* Users */}
      <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
        Usuários
      </h2>
      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Total" value={kpis.users.total} />
        <KpiCard label="Ativos este mês" value={kpis.users.activeThisMonth} />
      </div>

      {/* WhatsApp */}
      <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
        WhatsApp
      </h2>
      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Conectadas" value={kpis.whatsapp.connected} color="text-green-600" />
        <KpiCard label="Total" value={kpis.whatsapp.total} />
        <KpiCard
          label="Taxa de conexão"
          value={`${kpis.whatsapp.connectionRate}%`}
          color={kpis.whatsapp.connectionRate >= 70 ? 'text-green-600' : 'text-yellow-600'}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Plan distribution */}
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-sm font-semibold text-gray-700">Distribuição de planos</h2>
          <div className="space-y-2">
            {planOrder.map((plan) => {
              const count = kpis.plans[plan] ?? 0;
              const pct =
                kpis.tenants.total > 0
                  ? Math.round((count / kpis.tenants.total) * 100)
                  : 0;
              return (
                <div key={plan} className="flex items-center gap-3">
                  <span className="w-20 text-sm capitalize text-gray-600">{plan}</span>
                  <div className="flex-1 rounded-full bg-gray-100 h-2">
                    <div
                      className="h-2 rounded-full bg-primary-500"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="w-8 text-right text-sm text-gray-700">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
